/**
 * How far a download has got, under its buttons — every download in the product (owner request 2026-10-24, `docs/adr/9074`).
 *
 * The run's «Tutte le celle» file showed it first (`src/features/inseason/RunCellsDownload.tsx`): a large run takes minutes, and a
 * button that says «Preparazione…» for two minutes reads as stuck. The same words and the same bar now stand under every
 * download, in three phases:
 *
 *   • **preparing** — nothing heard yet: «Preparazione del file…», with `aria-busy`. Never a bar: there is nothing to measure.
 *   • **reading** — the rows received. Of the rows the platform stated, as a bar («12.000 di 436.950 righe ricevute»), when it
 *     stated them (`X-Bata-Row-Count`, platform ADR 6201); otherwise the rows alone, in words and no bar — «12.000 righe ricevute
 *     finora (12 pagine)» while a collection is walked page by page (`src/api/walk-progress.ts`). A total is never guessed.
 *   • **building** — the rows written into the file, of the rows it will hold when that is known (a CSV's table), as a bar;
 *     otherwise the rows alone (a workbook's rows run over every sheet, «Tecnico» and «Colonne» too).
 *
 * A bar is `role="progressbar"` with `aria-valuenow` of `aria-valuemax`, its words as `aria-valuetext`; words without a bar are
 * a `role="status"` live region. The words are the core catalogue's (`export.progress.*`), in it-IT, en-GB and es-419.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listenToPageWalks } from '../api/walk-progress';
import { formatInteger } from '../lib/format';

/** Where a download is. */
export type DownloadProgressState =
  | { readonly phase: 'preparing' }
  | { readonly phase: 'reading'; readonly received: number; readonly stated: number | null; readonly pages: number | null }
  | { readonly phase: 'building'; readonly received: number; readonly stated: number | null };

/** What a download's work says while it runs. */
export interface DownloadReporter {
  /** Rows received so far, of the rows the platform stated (`null`: it stated none) — for a builder that reads a stream. */
  readonly received: (received: number, stated: number | null) => void;
  /** Rows written into the file so far, of the rows it will hold when known — `runExport`'s `onProgress`. */
  readonly written: (rows: number, stated?: number | null) => void;
}

/**
 * `runExport`'s `onProgress` for a file of `format`: a CSV's rows are its table's, so the request's `rowCount` is the file's
 * and the rows written are a bar; a workbook's rows run over every sheet, so they are said alone.
 */
export function writtenProgress(reporter: DownloadReporter, format: string, request: { readonly rowCount?: number }): (rows: number) => void {
  const stated = format === 'xlsx' ? null : request.rowCount ?? null;
  return (rows) => reporter.written(rows, stated);
}

export interface DownloadTracking {
  /** Where the download is; `null` while none runs. */
  readonly progress: DownloadProgressState | null;
  /**
   * Run one download's work, hearing its progress: it starts «preparing», counts the pages any collection walk reads while it
   * runs, and takes what `work` reports. Ends with the work, however it ends.
   */
  readonly track: <T>(work: (reporter: DownloadReporter) => Promise<T>) => Promise<T>;
}

/** A download's progress, for a control that runs it. */
export function useDownloadProgress(): DownloadTracking {
  const [progress, setProgress] = useState<DownloadProgressState | null>(null);
  // The run under way: a report that arrives after it ended (a late page) is not drawn.
  const current = useRef(0);
  const track = useCallback(async <T,>(work: (reporter: DownloadReporter) => Promise<T>): Promise<T> => {
    const run = current.current + 1;
    current.current = run;
    let walked = { rows: 0, pages: 0 };
    let heard: 'pages' | 'stream' | 'building' | null = null;
    const set = (next: DownloadProgressState): void => { if (current.current === run) setProgress(next); };
    set({ phase: 'preparing' });
    const stop = listenToPageWalks((rows) => {
      if (heard !== null && heard !== 'pages') return;
      heard = 'pages';
      walked = { rows: walked.rows + rows, pages: walked.pages + 1 };
      set({ phase: 'reading', received: walked.rows, stated: null, pages: walked.pages });
    });
    const reporter: DownloadReporter = {
      received: (received, stated) => {
        if (heard === 'building') return;
        heard = 'stream';
        set({ phase: 'reading', received, stated, pages: null });
      },
      written: (rows, stated = null) => {
        heard = 'building';
        set({ phase: 'building', received: rows, stated });
      },
    };
    try {
      return await work(reporter);
    } finally {
      stop();
      if (current.current === run) setProgress(null);
    }
  }, []);
  return { progress, track };
}

export interface DownloadProgressProps {
  readonly received: number;
  readonly stated: number | null;
  /** The file has arrived and its rows are being written into it. */
  readonly building?: boolean;
  /** Pages walked so far, when the rows are read a page at a time and no total is stated. */
  readonly pages?: number | null;
  readonly [attribute: `data-${string}`]: string | undefined;
}

/**
 * «X di Y righe» while the file is read or written: a progress bar on the rows stated, or the rows alone when none were — then
 * no bar, only words, in a live region.
 */
export function DownloadProgress({ received, stated, building = false, pages = null, ...data }: DownloadProgressProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const n = (value: number): string => formatInteger(value, i18n.language);
  if (stated === null) {
    const text = building
      ? t('export.progress.buildingUnstated', { received: n(received) })
      : pages === null
        ? t('export.progress.receivedUnstated', { received: n(received) })
        : t('export.progress.receivedPages', { received: n(received), pages });
    return <p role="status" className="text-caption text-ink-secondary tabular-nums" data-download-progress={building ? 'building' : 'reading'} {...data}>{text}</p>;
  }
  const text = t(building ? 'export.progress.building' : 'export.progress.received', { received: n(received), stated: n(stated) });
  const share = stated === 0 ? 1 : Math.min(1, received / stated);
  return (
    <div className="flex max-w-prose flex-col gap-1" data-download-progress={building ? 'building' : 'reading'} {...data}>
      <div
        role="progressbar"
        aria-label={t(building ? 'export.progress.buildingLabel' : 'export.progress.label')}
        aria-valuemin={0}
        aria-valuemax={stated}
        aria-valuenow={received}
        aria-valuetext={text}
        className="bg-surface-constants h-2 w-full overflow-hidden"
      >
        <div className="bg-ink-primary h-full" style={{ inlineSize: `${Math.round(share * 1000) / 10}%` }} />
      </div>
      <span className="text-caption text-ink-secondary tabular-nums">{text}</span>
    </div>
  );
}

/** {@link DownloadProgress} for a {@link DownloadProgressState}: «preparing» is words with `aria-busy`, never a bar. */
export function DownloadProgressLine(
  { progress, ...data }: { readonly progress: DownloadProgressState | null; readonly [attribute: `data-${string}`]: string | undefined },
): React.JSX.Element | null {
  const { t } = useTranslation();
  if (progress === null) return null;
  if (progress.phase === 'preparing') {
    return (
      <p role="status" aria-busy="true" className="text-caption text-ink-secondary" data-download-progress="preparing" {...data}>
        {t('export.progress.preparing')}
      </p>
    );
  }
  return progress.phase === 'building'
    ? <DownloadProgress received={progress.received} stated={progress.stated} building {...data} />
    : <DownloadProgress received={progress.received} stated={progress.stated} pages={progress.pages} {...data} />;
}
